import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { showInkToast } from '../cms/InkToast';
import {
  CHANNEL_CANDIDATES,
  CHANNEL_CONNECT_FAILED,
  ChannelCandidate,
  ChannelExchange,
  normalizePlatform,
  startChannelAuth,
  takePendingResult,
} from '../../utils/socialConnect';
import { BlueskyConnect } from './BlueskyConnect';
import { Channel, channelMark, channelName, CONNECT_SOCIAL_CHANNELS } from './reach';

/** The channel a Reconnect sign-in should refresh, when it comes back in the list. */
export interface ReconnectTarget {
  platform: string;
  profileId: string;
}

/**
 * Connecting channels from a sign-in, shared by Reach → Channels and
 * Settings → Social: start(platform) opens the sign-in popup; when it comes
 * back with the channels that sign-in can post to, one is connected at once
 * and several are offered in the picker (render `picker`). Rows exist only
 * once the person connects.
 */
export function useChannelConnect({
  accountId,
  onConnected,
}: {
  accountId?: string | null;
  onConnected?: (rows: Channel[]) => void;
}) {
  const [connectMutation] = useMutation<{ connectSocialChannels: Channel[] }>(
    CONNECT_SOCIAL_CHANNELS
  );
  const [offer, setOffer] = useState<ChannelExchange | null>(null);
  const [busy, setBusy] = useState(false);
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  // Set by start() when the sign-in is a Reconnect of one row. Cleared once
  // that sign-in comes back, so a later Add doesn't reuse it.
  const reconnectRef = useRef<ReconnectTarget | null>(null);

  const connect = useCallback(
    async (exchange: ChannelExchange, picks: ChannelCandidate[], reconnect = false) => {
      if (!accountId || !picks.length) return;
      setBusy(true);
      try {
        const { data } = await connectMutation({
          variables: {
            input: {
              accountId,
              ticket: exchange.ticket,
              channels: picks.map((c) => ({ platform: c.platform, profileId: c.profileId })),
            },
          },
        });
        const rows = data?.connectSocialChannels || [];
        setOffer(null);
        onConnectedRef.current?.(rows);
        const verb = reconnect ? 'reconnected' : 'connected';
        showInkToast(
          rows.length === 1
            ? `${channelName(rows[0].platform)} ${verb}.`
            : `${rows.length} channels ${verb}.`
        );
      } catch (err) {
        showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
      } finally {
        setBusy(false);
      }
    },
    [accountId, connectMutation]
  );

  const receive = useCallback(
    (result: ChannelExchange) => {
      if (!result?.ticket || !result.channels?.length) return;
      const target = reconnectRef.current;
      reconnectRef.current = null;
      const match = target
        ? result.channels.find(
            (c) =>
              normalizePlatform(c.platform) === normalizePlatform(target.platform) &&
              c.profileId === target.profileId
          )
        : undefined;
      if (match) {
        void connect(result, [match], true);
        return;
      }
      if (result.channels.length === 1) void connect(result, result.channels);
      else setOffer(result);
    },
    [connect]
  );

  // The popup posts its result here; a copy also waits in localStorage for
  // the case where this window was gone (or reloaded) when the popup finished.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type === CHANNEL_CANDIDATES) {
        takePendingResult(); // this window has it now
        receive(data.result as ChannelExchange);
      } else if (data.type === CHANNEL_CONNECT_FAILED && data.message) {
        reconnectRef.current = null;
        showInkToast(String(data.message));
      }
    };
    window.addEventListener('message', onMessage);
    const waiting = takePendingResult();
    if (waiting) receive(waiting);
    return () => window.removeEventListener('message', onMessage);
  }, [receive]);

  const start = useCallback(
    async (platform: string, reconnect?: ReconnectTarget) => {
      if (!accountId) return;
      reconnectRef.current = reconnect || null;
      try {
        const popup = await startChannelAuth(platform, accountId);
        if (!popup) {
          reconnectRef.current = null;
          showInkToast('Allow pop-ups for this site to sign in to the channel.');
        }
      } catch (err) {
        reconnectRef.current = null;
        showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
      }
    },
    [accountId]
  );

  const picker = offer ? (
    <ChannelPicker
      key={offer.ticket}
      offer={offer}
      busy={busy}
      onCancel={() => setOffer(null)}
      onConnect={(picks) => void connect(offer, picks)}
    />
  ) : null;

  return { start, picker, connecting: busy };
}

/**
 * Reconnect for any screen that names an expired channel (Channels, the
 * Calendar's card, a campaign): the channel's own sign-in, refreshing that same
 * row. Bluesky signs in with a handle and an app password, not a window
 * (BlueskyConnect). Render `dialogs` once.
 */
export function useChannelReconnect({
  accountId,
  onConnected,
}: {
  accountId?: string | null;
  onConnected?: () => void;
}) {
  const { start, picker } = useChannelConnect({ accountId, onConnected });
  // handle is set when Reconnect opens it for a channel already on the list.
  const [bluesky, setBluesky] = useState<{ handle: string; reconnect: boolean } | null>(null);

  const reconnect = (c: Channel) => {
    const platform = c.platform.toLowerCase() === 'twitter' ? 'x' : c.platform.toLowerCase();
    if (platform === 'bluesky') {
      setBluesky({
        handle: (c.profileUsername || '').replace(/^@/, ''),
        reconnect: true,
      });
      return;
    }
    // An Instagram account linked to a Facebook Page refreshes through that
    // Facebook sign-in. Every other channel uses its own.
    const auth = platform === 'instagram' && c.metricsNeedReconnect ? 'facebook' : platform;
    void start(auth, c.profileId ? { platform, profileId: c.profileId } : undefined);
  };

  const dialogs = (
    <>
      {picker}
      {bluesky && accountId ? (
        <BlueskyConnect
          accountId={accountId}
          initialHandle={bluesky.handle}
          reconnect={bluesky.reconnect}
          onClose={() => setBluesky(null)}
          onConnected={() => {
            setBluesky(null);
            onConnected?.();
          }}
        />
      ) : null}
    </>
  );

  return {
    start,
    reconnect,
    addBluesky: () => setBluesky({ handle: '', reconnect: false }),
    dialogs,
  };
}

const key = (c: ChannelCandidate) => `${c.platform}:${c.profileId}`;

/** "Which of these do you want to connect?" — the channels one sign-in offers. */
export function ChannelPicker({
  offer,
  busy,
  onCancel,
  onConnect,
}: {
  offer: ChannelExchange;
  busy: boolean;
  onCancel: () => void;
  onConnect: (picks: ChannelCandidate[]) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(offer.channels.map(key)));
  const picks = useMemo(() => offer.channels.filter((c) => picked.has(key(c))), [offer, picked]);
  const platforms = Array.from(new Set(offer.channels.map((c) => c.platform)));
  const title =
    platforms.length === 1
      ? `Connect ${channelName(platforms[0])}`
      : `Connect ${platforms.map(channelName).join(' and ')}`;

  const toggle = (c: ChannelCandidate) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key(c))) next.delete(key(c));
      else next.add(key(c));
      return next;
    });

  return (
    <div className="ink-modal-back" role="dialog" aria-modal="true" aria-labelledby="chp-title">
      <div className="ink-modal chp">
        <div className="ink-modal-title" id="chp-title">
          {title}
        </div>
        <p className="ink-modal-copy">
          This sign-in can post to these channels. Pick the ones your agent may use.
        </p>
        <div className="chp-list">
          {offer.channels.map((c) => (
            <label className="chp-row" key={key(c)}>
              <input
                type="checkbox"
                checked={picked.has(key(c))}
                disabled={busy}
                onChange={() => toggle(c)}
              />
              <span className="rc-mark rc-mark-sm">{channelMark(c.platform)}</span>
              <span className="chp-name">
                <span>{c.name || c.username || c.profileId}</span>
                <span className="ink-muted">
                  {channelName(c.platform)}
                  {c.username ? ` · @${c.username.replace(/^@/, '')}` : ''}
                  {c.connected ? ' · Connected' : c.known ? ' · Was connected before' : ''}
                </span>
              </span>
            </label>
          ))}
        </div>
        <div className="ink-modal-actions">
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            disabled={busy}
            onClick={onCancel}
          >
            Not now
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={busy || !picks.length}
            onClick={() => onConnect(picks)}
          >
            {busy
              ? 'Connecting…'
              : picks.length === 1
                ? 'Connect 1 channel'
                : `Connect ${picks.length} channels`}
          </button>
        </div>
      </div>
    </div>
  );
}
